import type {
  Coaching,
  CoachingConfig,
  CoachingPartnership,
  CoachingPlan,
  CoachingSession,
} from '@efdigital/ll-addon-data';
import type {
  CoacheeEntry,
  CoachingCoachCoacheesModel,
} from '@efdigital/ll-xceed-v2/routes/coaching/coach/coachees';
import {
  type CoacheeSessionSummary,
  summarizeCoacheeSessions,
} from '@efdigital/ll-xceed-v2/utils/coaching/coach-coachees';
import Route from '@ember/routing/route';

/** One plan a coachee is working through with this coach. */
export type CoacheeEngagement = {
  partnership: CoachingPartnership;
  coachingPlan: CoachingPlan;
  coaching: Coaching;
  coachingConfig: CoachingConfig;
};

export type CoachingCoachCoacheesCoacheeModel = CoachingCoachCoacheesModel & {
  coachee: CoacheeEntry;
  /** Set for chemistry or chosen with this coachee. Prefer chosen. */
  messagePartnership?: CoachingPartnership;
  /** More than one only when the coachee works with this coach on several plans. */
  engagements: CoacheeEngagement[];
  /** Coaching time allocated across those plans, in minutes. */
  allocatedMinutes: number;
  summary: CoacheeSessionSummary<CoachingSession>;
};

type CoacheeParams = {
  coachee_id: string;
};

export default class CoachingCoachCoacheesCoacheeRoute extends Route<CoachingCoachCoacheesCoacheeModel> {
  async model(
    params: CoacheeParams
  ): Promise<CoachingCoachCoacheesCoacheeModel> {
    const coacheesModel = this.modelFor(
      'coaching.coach.coachees'
    ) as CoachingCoachCoacheesModel;

    const coachee = coacheesModel.coachees.find(
      (entry) => entry.coacheeUserId === params.coachee_id
    );

    if (!coachee) {
      throw new Error(`No coachee found for ${params.coachee_id}`);
    }

    // The partnership query includes the plan and its config, so these resolve
    // from the store rather than firing a request each.
    const engagements = await Promise.all(
      coachee.partnerships.map(async (partnership) => {
        const coachingPlan = (await partnership.coachingPlan)!;
        const coaching = (await coachingPlan.coaching)!;
        const coachingConfig = (await coaching.coachingConfig)!;

        return { partnership, coachingPlan, coaching, coachingConfig };
      })
    );

    return {
      ...coacheesModel,
      coachee,
      messagePartnership:
        coachee.partnerships.find(
          (partnership) => partnership.status === 'chosen'
        ) ??
        coachee.partnerships.find(
          (partnership) => partnership.status === 'chemistry'
        ),
      engagements,
      allocatedMinutes: engagements.reduce(
        (total, engagement) =>
          total + engagement.coachingConfig.coachingTimeMinutes,
        0
      ),
      summary: summarizeCoacheeSessions(coachee.sessions),
    };
  }
}
